import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'devextreme-react/button';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import DataGrid, { Column, Editing } from 'devextreme-react/data-grid';
import useApi from '../../hooks/useApi';
import Guid from '../../models/Guid';
import { IHistoriqueAccompagnateur } from './type';
import './popup-historique-accompagnateurs.css';

interface PopupHistoriqueAccompagnateursProps {
  visible: boolean;
  idClient: Guid;
  clientDisplayName: string;
  accompagnateursActuels: { nom?: string; prenom?: string; isDeleted?: boolean }[];
  onClose: () => void;
  onConfirm: (lignes: IHistoriqueAccompagnateur[]) => void;
}

const normaliserNom = (valeur?: string) => (valeur ?? '').trim().toLowerCase();

const accompagnateurDejaPresent = (
  accompagnateurs: PopupHistoriqueAccompagnateursProps['accompagnateursActuels'],
  nom: string,
  prenom: string,
) => accompagnateurs.some((accompagnateur) =>
  !accompagnateur.isDeleted
  && normaliserNom(accompagnateur.nom) === normaliserNom(nom)
  && normaliserNom(accompagnateur.prenom) === normaliserNom(prenom),
);

export default function PopupHistoriqueAccompagnateurs({
  visible,
  idClient,
  clientDisplayName,
  accompagnateursActuels,
  onClose,
  onConfirm,
}: PopupHistoriqueAccompagnateursProps) {
  const { getData } = useApi<IHistoriqueAccompagnateur[]>('client');
  const [historique, setHistorique] = useState<IHistoriqueAccompagnateur[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const accompagnateursRef = useRef(accompagnateursActuels);
  accompagnateursRef.current = accompagnateursActuels;

  useEffect(() => {
    if (!visible || !idClient || idClient.equals(Guid.empty)) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getData({ customPath: `client/${idClient}/accompagnateursHistorique`, returnOnly: true })
      .then((result) => {
        if (cancelled) return;
        const lignes = (result ?? []).map((entry) => ({
          ...entry,
          inclus: accompagnateurDejaPresent(accompagnateursRef.current, entry.nom, entry.prenom),
        }));
        setHistorique(lignes);
      })
      .catch(() => {
        if (!cancelled) setHistorique([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [getData, idClient, visible]);

  const setTousInclus = useCallback((inclus: boolean) => {
    setHistorique((prev) => prev.map((entry) => ({ ...entry, inclus })));
  }, []);

  const handleOk = useCallback(() => {
    onConfirm(historique);
    onClose();
  }, [historique, onClose, onConfirm]);

  const toolbarItems = useMemo(() => (
    <>
      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        options={{
          text: 'Ok',
          type: 'default',
          stylingMode: 'contained',
          onClick: handleOk,
        }}
      />
      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        options={{
          text: 'Annuler',
          type: 'default',
          stylingMode: 'contained',
          onClick: onClose,
        }}
      />
    </>
  ), [handleOk, onClose]);

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      dragEnabled
      resizeEnabled
      hideOnOutsideClick={false}
      showCloseButton
      showTitle
      title="Historique des accompagnateurs"
      width={520}
      height="auto"
      maxHeight="80vh"
    >
      <div className="historique-accompagnateurs-popup">
        <div className="historique-accompagnateurs-popup__header">
          <span className="historique-accompagnateurs-popup__client">{clientDisplayName}</span>
          <div className="historique-accompagnateurs-popup__actions">
            <Button text="Tous" stylingMode="outlined" onClick={() => setTousInclus(true)} />
            <Button text="Aucun" stylingMode="outlined" onClick={() => setTousInclus(false)} />
          </div>
        </div>
        <DataGrid
          dataSource={historique}
          keyExpr="idClient"
          showBorders
          height={320}
          noDataText={isLoading ? 'Chargement...' : 'Aucun accompagnateur dans l\'historique'}
        >
          <Editing mode="cell" allowUpdating allowAdding={false} allowDeleting={false} />
          <Column dataField="prenom" caption="Prénom" allowEditing={false} />
          <Column dataField="nom" caption="Nom" allowEditing={false} />
          <Column dataField="inclus" caption="Inclus?" dataType="boolean" width={90} alignment="center" />
        </DataGrid>
      </div>
      {toolbarItems}
    </Popup>
  );
}
